import { useApi } from './apiService';

export interface AgentHeartbeatStatusItem {
  tenantAgentId: string;
  agentStatus: string;
  heartbeatStatusMessage?: string;
  hostName?: string | null;
  ipAddress?: string | null;
}

export interface AgentHeartbeatStatusResponse {
  data: AgentHeartbeatStatusItem[];
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

export interface AgentLastHeartbeatData {
  tenantAgentId: string;
  tenantId: string;
  lastHeartBeatUtc: string;
  hostName: string | null;
  ipAddress: string | null;
  id: string;
  _ts: number;
  schemaVersion: string;
}

export interface AgentLastHeartbeatResponse {
  data: AgentLastHeartbeatData | null;
  message: string;
  isSuccess: boolean;
  errorCode: string | null;
}

// This shape can be used directly to control a "Last Heartbeat" modal in UI layers.
export interface AgentLastHeartbeatModalState {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  heartbeat: AgentLastHeartbeatData | null;
}

export const initialAgentLastHeartbeatModalState: AgentLastHeartbeatModalState = {
  isOpen: false,
  isLoading: false,
  error: null,
  heartbeat: null,
};

export const useAgentHeartBeatService = () => {
  const api = useApi();

  /**
   * Fetch heartbeat status for a list of agent IDs
   * @param tenantId - current tenant ID
   * @param agentIds - array of tenantAgentId strings
   * @returns heartbeat response array
   */
  const getAgentHeartbeatStatus = async (tenantId: string, agentIds: string[]) => {
    try {
      const response = await api.post(`tenantservice/TenantAgent/${tenantId}/check-agent-heartbeat`, agentIds);
      return response.data as AgentHeartbeatStatusResponse;
    } catch (error) {
      console.error('Error fetching heartbeat status:', error);
      throw error;
    }
  };

  /**
   * Fetch the latest heartbeat for a single tenant agent.
   * GET: tenantservice/TenantAgent/{tenantId}/{tenantAgentId}/get-agent-last-heartbeat
   */
  const getAgentLastHeartbeat = async (tenantId: string, tenantAgentId: string) => {
    try {
      const response = await api.post(`tenantservice/TenantAgent/${tenantId}/${tenantAgentId}/get-agent-last-heartbeat`);
      return response.data as AgentLastHeartbeatResponse;
    } catch (error) {
      console.error('Error fetching last heartbeat:', error);
      throw error;
    }
  };

  return { getAgentHeartbeatStatus, getAgentLastHeartbeat };
};
